import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { setupAdminAndUpdateMarli, getCurrentUsers, SetupResult } from '../utils/adminSetup';

const AdminSetup: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SetupResult | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const loadUsers = async () => {
    setLoadingUsers(true);
    const data = await getCurrentUsers();
    setUsers(data);
    setLoadingUsers(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    setResult(null);

    const setupResult = await setupAdminAndUpdateMarli();
    setResult(setupResult);

    // Reload users after setup
    await loadUsers();

    setLoading(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Configuração de Admin
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Esta página permite criar o usuário administrador e atualizar a role da Marli para psicóloga profissional.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            O que será feito:
          </Typography>
          <ul>
            <li>Criar usuário admin@psicosys.com com role de Administrador</li>
            <li>Atualizar marli@psicosys.com para role de Profissional (Psicóloga)</li>
          </ul>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSetup}
          disabled={loading}
          sx={{ mb: 3 }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
              Executando...
            </>
          ) : (
            'Executar Configuração'
          )}
        </Button>

        {result && (
          <Alert severity={result.success ? 'success' : 'error'} sx={{ mb: 3 }}>
            {result.message}
          </Alert>
        )}

        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          Usuários Atuais
        </Typography>

        {loadingUsers ? (
          <CircularProgress />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role?.display_name || user.role?.name || 'N/A'}
                        color={user.role?.name === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Ativo' : 'Inativo'}
                        color={user.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 4, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.dark" fontWeight={600}>
            Importante - Segurança:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            As senhas devem ser configuradas via variáveis de ambiente:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
            <li><code>REACT_APP_ADMIN_INITIAL_PASSWORD</code> - Senha do admin</li>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Usuários criados:</strong> admin@psicosys.com, marli@psicosys.com
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Nunca exponha senhas em código ou interfaces. Altere as senhas após o primeiro acesso.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminSetup;
